/**
 * Main App component with routing and layout system.
 * Updated to use Vuexy-style layout system.
 */

import { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ProtectedRoute, Toast, ErrorBoundary } from './components/common';
import { ConditionalLayout } from './components/layout/ConditionalLayout';

// Page Imports
import { DashboardPage } from './pages/DashboardPage';
import { DashboardCustomizationPage } from './pages/DashboardCustomizationPage';
import { ProjectListPage } from './pages/ProjectListPage';
import { ApiKeyListPage } from './pages/ApiKeyListPage';
import { AgentRegistryPage } from './pages/AgentRegistryPage';
import { AgentDetailPage } from './pages/AgentDetailPage';
import { AgentForm } from './components/agents/AgentForm';
import { SessionListPage } from './pages/SessionListPage';
import { SessionDetailPage } from './pages/SessionDetailPage';
import { MemoryListPage } from './pages/MemoryListPage';
import { MemoryDetailPage } from './pages/MemoryDetailPage';
import { SkillsPage } from './pages/SkillsPage';
import { ToolsListPage } from './pages/ToolsListPage';
import { ToolDetailPage } from './pages/ToolDetailPage';
import { ToolForm } from './components/tools/ToolForm';
import { WorkflowListPage } from './pages/WorkflowListPage';
import { WorkflowDetailPage } from './pages/WorkflowDetailPage';
import { WorkflowFormPage } from './pages/WorkflowFormPage';
import { ScheduleListPage } from './pages/ScheduleListPage';
import { ScheduleDetailPage } from './pages/ScheduleDetailPage';
import { ScheduleForm } from './components/schedules/ScheduleForm';
import { InvestigationListPage } from './pages/InvestigationListPage';
import { InvestigationDetailPage } from './pages/InvestigationDetailPage';
import { InvestigationTemplatesListPage } from './pages/InvestigationTemplatesListPage';
import { InvestigationForm } from './components/investigations/InvestigationForm';
import { InvestigationTemplateForm } from './components/investigations/InvestigationTemplateForm';
import { SafetyGatesListPage } from './pages/SafetyGatesListPage';
import { ApprovalQueuePage } from './pages/ApprovalQueuePage';
import { SafetyGateForm } from './components/safetyGates/SafetyGateForm';
import { McpServersListPage } from './pages/McpServersListPage';
import { McpServerDetailPage } from './pages/McpServerDetailPage';
import { McpServerForm } from './components/mcpServers/McpServerForm';
import { ExternalSystemsListPage } from './pages/ExternalSystemsListPage';
import { ExternalSystemDetailPage } from './pages/ExternalSystemDetailPage';
import { ExternalSystemForm } from './components/externalSystems/ExternalSystemForm';
import { ConnectorsListPage } from './pages/ConnectorsListPage';
import { ConnectorDetailPage } from './pages/ConnectorDetailPage';
import { ConnectorInstanceWizardPage } from './pages/ConnectorInstanceWizardPage';
import { ConnectorInstanceDetailPage } from './pages/ConnectorInstanceDetailPage';
import { TaskListPage } from './pages/TaskListPage';
import { TaskDetailPage } from './pages/TaskDetailPage';
import { ExternalTaskSystemForm } from './components/hitl/ExternalTaskSystemForm';
import { UsageAnalyticsPage } from './pages/UsageAnalyticsPage';
import { ModelCatalogPage } from './pages/ModelCatalogPage';
import { LlmProviderConfigPage } from './pages/LlmProviderConfigPage';
import { UserSettingsPage } from './pages/UserSettingsPage';
import { TenantSettingsPage } from './pages/TenantSettingsPage';
import { ProjectSettingsPage } from './pages/ProjectSettingsPage';
import { OrchestratorPage } from './pages/OrchestratorPage';
import { OrchestratorRunPage } from './pages/OrchestratorRunPage';
import { AgentFunctionsListPage } from './pages/AgentFunctionsListPage';
import { AgentFunctionDetailPage } from './pages/AgentFunctionDetailPage';
import { AgentFunctionExecutePage } from './pages/AgentFunctionExecutePage';
import { AgentFunctionRequestsListPage } from './pages/AgentFunctionRequestsListPage';
import { AgentFunctionRequestDetailPage } from './pages/AgentFunctionRequestDetailPage';
import { AgentFunctionForm } from './components/agentFunctions/AgentFunctionForm';
import { ApiExplorerPage } from './pages/ApiExplorerPage';
import { ApiExplorerDetailPage } from './pages/ApiExplorerDetailPage';
import { ROUTES } from './utils/routes';
import { callbackPath } from './config/authConfig';
import { SignInCallbackPage } from './pages/SignInCallbackPage';
import { settingsService } from './services/settingsService';
import { useAuthStore } from './store/authStore';
import { useSettingsStore } from './store/settingsStore';
import { useContextStore } from './store/contextStore';
import { SignalRProvider } from './providers/SignalRProvider';
import { MsalProviderWithAuth } from './providers/MsalProviderWithAuth';

// Type Imports
import type { SystemMode } from './@core/types';

// Configure React Query
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  },
});

/**
 * Detect system color scheme preference
 */
const getSystemMode = (): SystemMode => {
  if (typeof window !== 'undefined' && window.matchMedia) {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  }
  return 'light';
};

/**
 * Main App component
 */
function App() {
  const systemMode = getSystemMode();
  const { isAuthenticated, token } = useAuthStore();
  const { updateSettings } = useSettingsStore();

  const loadModules = useContextStore((s) => s.loadModules);
  const evaluateGlobalAccess = useContextStore((s) => s.evaluateGlobalAccess);

  useEffect(() => {
    if (import.meta.env.DEV) console.log('[App] auth state', { isAuthenticated });
  }, [isAuthenticated]);

  // Load enabled modules from API (AllowAnonymous) so menu and route guard can filter
  useEffect(() => {
    loadModules();
  }, [loadModules]);

  useEffect(() => {
    evaluateGlobalAccess(token);
  }, [token, evaluateGlobalAccess]);

  // Best-effort: load persisted user settings and apply theme.
  // This keeps the UI in sync with backend preferences after login.
  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }

    let cancelled = false;
    settingsService
      .getMyUserSettings()
      .then((s) => {
        if (cancelled) return;
        if (s.theme === 'light' || s.theme === 'dark' || s.theme === 'system') {
          updateSettings({ mode: s.theme });
        }
      })
      .catch(() => {
        // Ignore errors here (backend may be unavailable in dev); user settings page can retry.
      });

    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, updateSettings]);

  return (
    <ErrorBoundary>
      <BrowserRouter
        future={{
          v7_startTransition: true,
          v7_relativeSplatPath: true,
        }}
      >
        <MsalProviderWithAuth>
          <QueryClientProvider client={queryClient}>
            <SignalRProvider>
            <ConditionalLayout systemMode={systemMode}>
              <Routes>
              {/* B2C redirect callback – stay here while SsoAuthHandler sets auth, then it navigates to home */}
              <Route path={callbackPath} element={<SignInCallbackPage />} />

              {/* Protected Routes */}
              <Route
                path={ROUTES.HOME}
                element={
                  <ProtectedRoute>
                    <DashboardPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path={ROUTES.DASHBOARD_CUSTOMIZE}
                element={
                  <ProtectedRoute>
                    <DashboardCustomizationPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path={ROUTES.TENANTS}
                element={
                  <ProtectedRoute>
                    <Navigate to={ROUTES.HOME} replace />
                  </ProtectedRoute>
                }
              />
              <Route
                path={ROUTES.PROJECTS}
                element={
                  <ProtectedRoute>
                    <ProjectListPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path={ROUTES.API_KEYS}
                element={
                  <ProtectedRoute>
                    <ApiKeyListPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path={ROUTES.AGENTS}
                element={
                  <ProtectedRoute>
                    <AgentRegistryPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path={ROUTES.AGENT_NEW}
                element={
                  <ProtectedRoute>
                    <AgentForm />
                  </ProtectedRoute>
                }
              />
              <Route
                path={ROUTES.AGENT_EDIT}
                element={
                  <ProtectedRoute>
                    <AgentForm />
                  </ProtectedRoute>
                }
              />
              <Route
                path={ROUTES.AGENT_DETAIL}
                element={
                  <ProtectedRoute>
                    <AgentDetailPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path={ROUTES.SESSIONS}
                element={
                  <ProtectedRoute>
                    <SessionListPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path={ROUTES.SESSION_DETAIL}
                element={
                  <ProtectedRoute>
                    <SessionDetailPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path={ROUTES.MEMORIES}
                element={
                  <ProtectedRoute>
                    <MemoryListPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path={ROUTES.MEMORY_DETAIL}
                element={
                  <ProtectedRoute>
                    <MemoryDetailPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path={ROUTES.ORCHESTRATOR}
                element={
                  <ProtectedRoute>
                    <OrchestratorPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path={ROUTES.ORCHESTRATOR_RUN}
                element={
                  <ProtectedRoute>
                    <OrchestratorRunPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path={ROUTES.AGENT_FUNCTIONS}
                element={
                  <ProtectedRoute>
                    <AgentFunctionsListPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path={ROUTES.AGENT_FUNCTION_NEW}
                element={
                  <ProtectedRoute>
                    <AgentFunctionForm />
                  </ProtectedRoute>
                }
              />
              <Route
                path={ROUTES.AGENT_FUNCTION_EDIT}
                element={
                  <ProtectedRoute>
                    <AgentFunctionForm />
                  </ProtectedRoute>
                }
              />
              <Route
                path={ROUTES.AGENT_FUNCTION_DETAIL}
                element={
                  <ProtectedRoute>
                    <AgentFunctionDetailPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path={ROUTES.AGENT_FUNCTION_EXECUTE}
                element={
                  <ProtectedRoute>
                    <AgentFunctionExecutePage />
                  </ProtectedRoute>
                }
              />
              <Route
                path={ROUTES.AGENT_FUNCTION_REQUESTS}
                element={
                  <ProtectedRoute>
                    <AgentFunctionRequestsListPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path={ROUTES.AGENT_FUNCTION_REQUEST_DETAIL}
                element={
                  <ProtectedRoute>
                    <AgentFunctionRequestDetailPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path={ROUTES.API_EXPLORER}
                element={
                  <ProtectedRoute>
                    <ApiExplorerPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path={ROUTES.API_EXPLORER_DETAIL}
                element={
                  <ProtectedRoute>
                    <ApiExplorerDetailPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path={ROUTES.SKILLS}
                element={
                  <ProtectedRoute>
                    <SkillsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path={ROUTES.TOOLS}
                element={
                  <ProtectedRoute>
                    <ToolsListPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path={ROUTES.TOOL_NEW}
                element={
                  <ProtectedRoute>
                    <ToolForm />
                  </ProtectedRoute>
                }
              />
              <Route
                path={ROUTES.TOOL_EDIT}
                element={
                  <ProtectedRoute>
                    <ToolForm />
                  </ProtectedRoute>
                }
              />
              <Route
                path={ROUTES.TOOL_DETAIL}
                element={
                  <ProtectedRoute>
                    <ToolDetailPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path={ROUTES.WORKFLOWS}
                element={
                  <ProtectedRoute>
                    <WorkflowListPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path={ROUTES.WORKFLOW_DETAIL}
                element={
                  <ProtectedRoute>
                    <WorkflowDetailPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path={ROUTES.WORKFLOW_NEW}
                element={
                  <ProtectedRoute>
                    <WorkflowFormPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path={ROUTES.WORKFLOW_EDIT}
                element={
                  <ProtectedRoute>
                    <WorkflowFormPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path={ROUTES.SCHEDULES}
                element={
                  <ProtectedRoute>
                    <ScheduleListPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path={ROUTES.SCHEDULE_NEW}
                element={
                  <ProtectedRoute>
                    <ScheduleForm />
                  </ProtectedRoute>
                }
              />
              <Route
                path={ROUTES.SCHEDULE_EDIT}
                element={
                  <ProtectedRoute>
                    <ScheduleForm />
                  </ProtectedRoute>
                }
              />
              <Route
                path={ROUTES.SCHEDULE_DETAIL}
                element={
                  <ProtectedRoute>
                    <ScheduleDetailPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path={ROUTES.INVESTIGATIONS}
                element={
                  <ProtectedRoute>
                    <InvestigationListPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path={ROUTES.INVESTIGATION_NEW}
                element={
                  <ProtectedRoute>
                    <InvestigationForm />
                  </ProtectedRoute>
                }
              />
              <Route
                path={ROUTES.INVESTIGATION_EDIT}
                element={
                  <ProtectedRoute>
                    <InvestigationForm />
                  </ProtectedRoute>
                }
              />
              <Route
                path={ROUTES.INVESTIGATION_DETAIL}
                element={
                  <ProtectedRoute>
                    <InvestigationDetailPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path={ROUTES.INVESTIGATION_TEMPLATES}
                element={
                  <ProtectedRoute>
                    <InvestigationTemplatesListPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path={ROUTES.INVESTIGATION_TEMPLATE_NEW}
                element={
                  <ProtectedRoute>
                    <InvestigationTemplateForm />
                  </ProtectedRoute>
                }
              />
              <Route
                path={ROUTES.INVESTIGATION_TEMPLATE_EDIT}
                element={
                  <ProtectedRoute>
                    <InvestigationTemplateForm />
                  </ProtectedRoute>
                }
              />
              <Route
                path={ROUTES.SAFETY_GATES}
                element={
                  <ProtectedRoute>
                    <SafetyGatesListPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path={ROUTES.SAFETY_GATE_NEW}
                element={
                  <ProtectedRoute>
                    <SafetyGateForm />
                  </ProtectedRoute>
                }
              />
              <Route
                path={ROUTES.SAFETY_GATE_EDIT}
                element={
                  <ProtectedRoute>
                    <SafetyGateForm />
                  </ProtectedRoute>
                }
              />
              <Route
                path={ROUTES.APPROVAL_QUEUE}
                element={
                  <ProtectedRoute>
                    <ApprovalQueuePage />
                  </ProtectedRoute>
                }
              />
              <Route
                path={ROUTES.MCP_SERVERS}
                element={
                  <ProtectedRoute>
                    <McpServersListPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path={ROUTES.MCP_SERVER_NEW}
                element={
                  <ProtectedRoute>
                    <McpServerForm />
                  </ProtectedRoute>
                }
              />
              <Route
                path={ROUTES.MCP_SERVER_EDIT}
                element={
                  <ProtectedRoute>
                    <McpServerForm />
                  </ProtectedRoute>
                }
              />
              <Route
                path={ROUTES.MCP_SERVER_DETAIL}
                element={
                  <ProtectedRoute>
                    <McpServerDetailPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path={ROUTES.EXTERNAL_SYSTEMS}
                element={
                  <ProtectedRoute>
                    <ExternalSystemsListPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path={ROUTES.EXTERNAL_SYSTEM_DETAIL}
                element={
                  <ProtectedRoute>
                    <ExternalSystemDetailPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path={ROUTES.EXTERNAL_SYSTEM_NEW}
                element={
                  <ProtectedRoute>
                    <ExternalSystemForm />
                  </ProtectedRoute>
                }
              />
              <Route
                path={ROUTES.EXTERNAL_SYSTEM_EDIT}
                element={
                  <ProtectedRoute>
                    <ExternalSystemForm />
                  </ProtectedRoute>
                }
              />
              <Route
                path={ROUTES.CONNECTORS}
                element={
                  <ProtectedRoute>
                    <ConnectorsListPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path={ROUTES.CONNECTOR_DETAIL}
                element={
                  <ProtectedRoute>
                    <ConnectorDetailPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path={ROUTES.CONNECTOR_INSTANCE_NEW}
                element={
                  <ProtectedRoute>
                    <ConnectorInstanceWizardPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path={ROUTES.CONNECTOR_INSTANCE_DETAIL}
                element={
                  <ProtectedRoute>
                    <ConnectorInstanceDetailPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path={ROUTES.TASKS}
                element={
                  <ProtectedRoute>
                    <TaskListPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path={ROUTES.TASK_DETAIL}
                element={
                  <ProtectedRoute>
                    <TaskDetailPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path={ROUTES.TASK_NEW}
                element={
                  <ProtectedRoute>
                    <TaskDetailPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path={ROUTES.EXTERNAL_TASK_SYSTEMS}
                element={
                  <ProtectedRoute>
                    <TaskListPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path={ROUTES.EXTERNAL_TASK_SYSTEM_NEW}
                element={
                  <ProtectedRoute>
                    <ExternalTaskSystemForm />
                  </ProtectedRoute>
                }
              />
              <Route
                path={ROUTES.EXTERNAL_TASK_SYSTEM_EDIT}
                element={
                  <ProtectedRoute>
                    <ExternalTaskSystemForm />
                  </ProtectedRoute>
                }
              />
              <Route
                path={ROUTES.USAGE_ANALYTICS}
                element={
                  <ProtectedRoute>
                    <UsageAnalyticsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path={ROUTES.MODEL_CATALOG}
                element={
                  <ProtectedRoute>
                    <ModelCatalogPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path={ROUTES.LLM_PROVIDER_CONFIGS}
                element={
                  <ProtectedRoute>
                    <LlmProviderConfigPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path={ROUTES.SETTINGS_USER}
                element={
                  <ProtectedRoute>
                    <UserSettingsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path={ROUTES.SETTINGS_TENANT}
                element={
                  <ProtectedRoute>
                    <TenantSettingsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path={ROUTES.SETTINGS_PROJECT}
                element={
                  <ProtectedRoute>
                    <ProjectSettingsPage />
                  </ProtectedRoute>
                }
              />
              <Route path="*" element={<Navigate to={ROUTES.HOME} replace />} />
              </Routes>
              <Toast />
            </ConditionalLayout>
        </SignalRProvider>
      </QueryClientProvider>
        </MsalProviderWithAuth>
      </BrowserRouter>
    </ErrorBoundary>
  );
}

export default App;
